import React, { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { Button } from "../components/ui/button";
import { Shield, CheckCircle, AlertCircle } from "lucide-react";
import apiClient from "../lib/apiClient";
import AdminLogin from "./AdminLogin";

const Setup = () => {
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");
  const [data, setData] = useState(null);
  const [setupRequired, setSetupRequired] = useState(true);
  const [locked, setLocked] = useState(false);

  useEffect(() => {
    const loadSetup = async () => {
      try {
        setLoading(true);
        
        const statusData = await apiClient.get('/api/auth/setup-status');
        const isRequired = statusData?.setup_required ?? true;
        const isAuthenticated = Boolean(statusData?.authenticated);
        setSetupRequired(isRequired);

        if (!isRequired && !isAuthenticated) {
          setLocked(true);
          return;
        }

        const setupData = await apiClient.get('/api/auth/setup');
        setData(setupData);
        
      } catch (err) {
        setError(err.message || "Unable to load setup.");
      } finally {
        setLoading(false);
      }
    };

    loadSetup();
  }, []);

  if (locked) {
    return <AdminLogin />;
  }

  return (
    <div className="min-h-screen bg-black text-white">
      <section className="pt-40 pb-24 px-6">
        <div className="max-w-3xl mx-auto">
          {/* Header */}
          <div className="mb-8">
            <div className="inline-flex items-center justify-center w-16 h-16 bg-cyan-500/10 rounded-2xl border border-cyan-500/20 mb-4">
              <Shield className="w-8 h-8 text-cyan-500" />
            </div>
            <h1 className="text-3xl md:text-4xl font-bold mb-4">Admin TOTP Setup</h1>
            <p className="text-neutral-400">
              Scan the QR code with your authenticator app (Microsoft Authenticator, Google Authenticator, etc.)
            </p>
          </div>

          {/* Loading State */}
          {loading && (
            <div className="flex items-center justify-center py-12">
              <div className="text-center">
                <div className="inline-block h-8 w-8 animate-spin rounded-full border-4 border-solid border-cyan-500 border-r-transparent mb-4"></div>
                <p className="text-neutral-400">Loading setup...</p>
              </div>
            </div>
          )}

          {/* Error State */}
          {!loading && error && (
            <div className="border border-red-500/40 bg-red-500/10 rounded-xl p-6 flex items-start gap-3">
              <AlertCircle className="w-5 h-5 text-red-400 mt-0.5 flex-shrink-0" />
              <div>
                <p className="text-red-200 font-medium mb-1">Setup Error</p>
                <p className="text-red-300/80 text-sm">{error}</p>
              </div>
            </div>
          )}

          {/* Success State - Show Setup Data */}
          {!loading && !error && data && (
            <div className="space-y-6">
              {/* Setup Status Banner */}
              {!setupRequired && (
                <div className="border border-green-500/40 bg-green-500/10 rounded-xl p-4 flex items-start gap-3">
                  <CheckCircle className="w-5 h-5 text-green-400 mt-0.5 flex-shrink-0" />
                  <div>
                    <p className="text-green-200 font-medium mb-1">Already Configured</p>
                    <p className="text-green-300/80 text-sm">
                      TOTP is already set up for this account. You can scan the QR code again if needed.
                    </p>
                  </div>
                </div>
              )}

              {/* Username */}
              <div className="rounded-2xl border border-neutral-800 bg-neutral-900/60 p-6">
                <p className="text-sm text-neutral-400 mb-2">Username</p>
                <p className="text-lg font-semibold">{data.username}</p>
              </div>

              {/* QR Code */}
              <div className="rounded-2xl border border-neutral-800 bg-neutral-900/60 p-6">
                <p className="text-sm text-neutral-400 mb-4">Scan QR Code with Authenticator App</p>
                <div className="bg-white inline-flex p-4 rounded-xl">
                  <img
                    src={data.qr_code}
                    alt="Authenticator QR"
                    className="w-64 h-64 object-contain"
                  />
                </div>
                <div className="mt-4 p-4 bg-neutral-800/50 rounded-lg border border-neutral-700">
                  <p className="text-xs text-neutral-400 mb-2">Recommended Apps:</p>
                  <ul className="text-sm text-neutral-300 space-y-1">
                    <li>• Microsoft Authenticator</li>
                    <li>• Google Authenticator</li>
                    <li>• Authy</li>
                  </ul>
                </div>
              </div>

              {/* Secret Key */}
              <div className="rounded-2xl border border-neutral-800 bg-neutral-900/60 p-6">
                <p className="text-sm text-neutral-400 mb-2">Secret Key (Manual Entry)</p>
                <p className="text-sm font-mono break-all bg-neutral-800 p-3 rounded-lg border border-neutral-700">
                  {data.secret}
                </p>
                <div className="mt-4 flex gap-3">
                  <Button
                    type="button"
                    className="bg-cyan-500 hover:bg-cyan-600 text-black font-medium"
                    onClick={() => {
                      navigator.clipboard?.writeText(data.secret);
                      // Could add toast notification here
                    }}
                  >
                    Copy Secret
                  </Button>
                  <Link to="/admin/login">
                    <Button
                      type="button"
                      variant="outline"
                      className="border-neutral-700 text-white hover:bg-neutral-800"
                    >
                      Continue to Login →
                    </Button>
                  </Link>
                </div>
              </div>

              {/* Instructions */}
              <div className="rounded-2xl border border-neutral-800 bg-neutral-900/60 p-6">
                <h3 className="font-semibold mb-3">Setup Instructions</h3>
                <ol className="space-y-2 text-sm text-neutral-300">
                  <li className="flex gap-2">
                    <span className="text-cyan-400 font-semibold">1.</span>
                    <span>Open your authenticator app</span>
                  </li>
                  <li className="flex gap-2">
                    <span className="text-cyan-400 font-semibold">2.</span>
                    <span>Scan the QR code or manually enter the secret key</span>
                  </li>
                  <li className="flex gap-2">
                    <span className="text-cyan-400 font-semibold">3.</span>
                    <span>Your app will generate a 6-digit code</span>
                  </li>
                  <li className="flex gap-2">
                    <span className="text-cyan-400 font-semibold">4.</span>
                    <span>Use that code to log in at the admin login page</span>
                  </li>
                </ol>
              </div>
            </div>
          )}

          {/* Back Link */}
          <div className="mt-8 text-center">
            <Link
              to="/"
              className="text-sm text-neutral-500 hover:text-neutral-400 transition-colors"
            >
              ← Back to Portfolio
            </Link>
          </div>
        </div>
      </section>
    </div>
  );
};

export default Setup;
